/**
 * Capture-everything analytics client (per CEO 2026-05-25).
 *
 * Two event types only: `view` (route mount) and `click` (any tap). All
 * events land in Cassandra (`test_keyspace.customer_events`) via the
 * customer_app v1 backend endpoint POST /api/customer/v1/events/batch.
 *
 * - session_id: UUID generated on first load, persisted in sessionStorage so
 *   refresh keeps it, but a new tab gets its own session.
 * - device_id : UUID generated once, persisted in localStorage forever.
 * - Transport: navigator.sendBeacon (falls back to fetch keepalive).
 * - Batch    : up to 25 events or 10s flush, whichever comes first.
 * - Attribution: captured from URL on first load (utm_*, gclid, fbclid),
 *   persisted in sessionStorage so every event includes it.
 * - Clarity  : on Clarity ready, tag the session + POST the replay URL to
 *   /api/customer/v1/clarity/session.
 *
 * The module is import-time safe — calling `initAnalytics()` from
 * `src/index.js` arms it; everything before init is silently no-op.
 *
 * No Mixpanel/PostHog/Segment — Cassandra is the SoT.
 */

const EVENTS_BATCH_PATH = '/api/customer/v1/events/batch';
const CLARITY_SESSION_PATH = '/api/customer/v1/clarity/session';

const SESSION_STORAGE_KEYS = {
  sessionId: 'rnr_analytics_session_id',
  attribution: 'rnr_analytics_attribution',
  claritySessionId: 'rnr_clarity_session_id',
  claritySessionUrl: 'rnr_clarity_session_url',
  claritySessionPosted: 'rnr_clarity_session_posted',
};

const LOCAL_STORAGE_KEYS = {
  deviceId: 'rnr_analytics_device_id',
  optOut: 'rnr_analytics_opt_out',
};

const FLUSH_AT_QUEUE_SIZE = 25;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH_TO_SEND = 50; // server cap
const MAX_PERSISTED_QUEUE = 200;

const CLARITY_PROJECT_ID = 'vrx21bds99';

// -----------------------------------------------------------------------------
//   Module state
// -----------------------------------------------------------------------------

let inMemoryQueue = [];
let currentPhone = null;
let flushTimer = null;
let isInitialized = false;
let isFlushInFlight = false;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// -----------------------------------------------------------------------------
//   Identity helpers
// -----------------------------------------------------------------------------

function uuidV4() {
  if (
    isBrowser &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    try {
      return window.crypto.randomUUID();
    } catch (_err) {
      // fall through
    }
  }
  // Fallback when crypto.randomUUID is unavailable (older Safari).
  const hex = (n) => n.toString(16).padStart(2, '0');
  const bytes = new Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const b = bytes.map(hex);
  return `${b.slice(0, 4).join('')}-${b.slice(4, 6).join('')}-${b
    .slice(6, 8)
    .join('')}-${b.slice(8, 10).join('')}-${b.slice(10, 16).join('')}`;
}

function safeSessionGet(key) {
  if (!isBrowser) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (_err) {
    return null;
  }
}

function safeSessionSet(key, value) {
  if (!isBrowser) return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (_err) {
    /* quota / private mode — swallow */
  }
}

function safeLocalGet(key) {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (_err) {
    return null;
  }
}

function safeLocalSet(key, value) {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (_err) {
    /* swallow */
  }
}

function ensureSessionId() {
  if (!isBrowser) return '';
  const existing = safeSessionGet(SESSION_STORAGE_KEYS.sessionId);
  if (existing) return existing;
  const fresh = uuidV4();
  safeSessionSet(SESSION_STORAGE_KEYS.sessionId, fresh);
  return fresh;
}

function ensureDeviceId() {
  if (!isBrowser) return '';
  const existing = safeLocalGet(LOCAL_STORAGE_KEYS.deviceId);
  if (existing) return existing;
  const fresh = uuidV4();
  safeLocalSet(LOCAL_STORAGE_KEYS.deviceId, fresh);
  return fresh;
}

function isOptedOut() {
  return safeLocalGet(LOCAL_STORAGE_KEYS.optOut) === '1';
}

// -----------------------------------------------------------------------------
//   Attribution capture
// -----------------------------------------------------------------------------

function captureAttributionOnce() {
  if (!isBrowser) return;
  try {
    const existing = safeSessionGet(SESSION_STORAGE_KEYS.attribution);
    if (existing) return; // already captured this session
    const urlParams = new URLSearchParams(window.location.search);
    const attribution = {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_content: urlParams.get('utm_content') || null,
      gclid: urlParams.get('gclid') || null,
      fbclid: urlParams.get('fbclid') || null,
      landing_url: window.location.href,
      referrer: (document && document.referrer) || '',
    };
    safeSessionSet(SESSION_STORAGE_KEYS.attribution, JSON.stringify(attribution));
  } catch (_err) {
    /* swallow */
  }
}

function getAttribution() {
  const raw = safeSessionGet(SESSION_STORAGE_KEYS.attribution);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

// -----------------------------------------------------------------------------
//   Queue + transport
// -----------------------------------------------------------------------------

function persistQueue() {
  if (!isBrowser) return;
  try {
    const capped =
      inMemoryQueue.length > MAX_PERSISTED_QUEUE
        ? inMemoryQueue.slice(-MAX_PERSISTED_QUEUE)
        : inMemoryQueue;
    window.sessionStorage.setItem(
      'rnr_analytics_queue',
      JSON.stringify(capped),
    );
  } catch (_err) {
    /* swallow */
  }
}

function loadPersistedQueue() {
  if (!isBrowser) return [];
  try {
    const raw = window.sessionStorage.getItem('rnr_analytics_queue');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

function buildEnvelope(type, props) {
  const claritySessionUrl = safeSessionGet(SESSION_STORAGE_KEYS.claritySessionUrl);
  return {
    type,
    ts: new Date().toISOString(),
    platform: 'web',
    app_version: null,
    session_id: ensureSessionId(),
    device_id: ensureDeviceId(),
    customer_phone: currentPhone,
    surface: props.surface || (isBrowser ? window.location.pathname : null),
    target: props.target || null,
    clarity_session_url: claritySessionUrl || null,
    attribution: getAttribution(),
    props: props.extra || null,
  };
}

function sendBatch(events) {
  if (!isBrowser || events.length === 0) return Promise.resolve(true);
  const url = EVENTS_BATCH_PATH;
  const body = JSON.stringify({ events });

  // navigator.sendBeacon is the lowest-overhead path and survives page-unload.
  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return Promise.resolve(true);
    }
  } catch (_err) {
    /* fall through to fetch */
  }

  // Fallback: fetch with keepalive so an unload-time call still delivers.
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'omit',
  })
    .then((res) => res.ok)
    .catch(() => false);
}

async function flush() {
  if (isFlushInFlight) return;
  if (inMemoryQueue.length === 0) return;
  isFlushInFlight = true;
  try {
    while (inMemoryQueue.length > 0) {
      const batch = inMemoryQueue.slice(0, MAX_BATCH_TO_SEND);
      // eslint-disable-next-line no-await-in-loop
      const ok = await sendBatch(batch);
      if (ok) {
        inMemoryQueue = inMemoryQueue.slice(batch.length);
        persistQueue();
      } else {
        // Keep the queue around; the next flush will retry.
        persistQueue();
        break;
      }
    }
  } finally {
    isFlushInFlight = false;
  }
}

// -----------------------------------------------------------------------------
//   Public API
// -----------------------------------------------------------------------------

export function track(type, propsOrInput) {
  if (!isBrowser) return;
  if (isOptedOut()) return;
  if (type !== 'view' && type !== 'click') return;
  const props = propsOrInput && typeof propsOrInput === 'object' ? propsOrInput : {};
  try {
    const envelope = buildEnvelope(type, props);
    inMemoryQueue.push(envelope);
    if (inMemoryQueue.length >= FLUSH_AT_QUEUE_SIZE) {
      void flush();
    } else {
      persistQueue();
    }
  } catch (_err) {
    /* swallow — analytics must never break the site */
  }
}

export function identify(phone) {
  currentPhone = phone ? String(phone) : null;
  // Tag the Clarity session with our session id + phone so a Clarity replay
  // can be cross-referenced from the dashboard.
  try {
    if (isBrowser && window.clarity && typeof window.clarity === 'function') {
      const sessionId = ensureSessionId();
      window.clarity('set', 'rnr_session_id', sessionId);
      if (phone) {
        // hashed-or-raw identifier — using the raw 10-digit phone is fine for
        // an internal Clarity dashboard.
        window.clarity('identify', String(phone), sessionId);
        window.clarity('set', 'rnr_customer_phone', String(phone));
      }
    }
  } catch (_err) {
    /* swallow */
  }
}

export function pageView(route) {
  track('view', { surface: route || (isBrowser ? window.location.pathname : null) });
}

/**
 * Derive a "target" label for a click event by walking up from the event
 * target. Order: `data-track-id` > `aria-label` > nearest <button>/<a>
 * trimmed text > `id`. Capped at 80 chars.
 */
function deriveClickTarget(eventTarget) {
  if (!eventTarget || !isBrowser) return null;
  let element = eventTarget;
  let depth = 0;
  while (element && depth < 6) {
    if (element.dataset && element.dataset.trackId) {
      return String(element.dataset.trackId).slice(0, 80);
    }
    const ariaLabel = element.getAttribute && element.getAttribute('aria-label');
    if (ariaLabel) return String(ariaLabel).slice(0, 80);
    const tagName = (element.tagName || '').toLowerCase();
    if (tagName === 'button' || tagName === 'a') {
      const text = (element.textContent || '').trim().replace(/\s+/g, ' ');
      if (text) return text.slice(0, 80);
    }
    if (element.id) return `#${String(element.id).slice(0, 78)}`;
    element = element.parentElement;
    depth += 1;
  }
  return null;
}

function installClickListener() {
  if (!isBrowser) return;
  document.body.addEventListener(
    'click',
    (event) => {
      try {
        const target = deriveClickTarget(event.target);
        track('click', {
          surface: window.location.pathname,
          target,
        });
      } catch (_err) {
        /* swallow */
      }
    },
    { capture: true, passive: true },
  );
}

// -----------------------------------------------------------------------------
//   Microsoft Clarity association
// -----------------------------------------------------------------------------

/**
 * Pull Clarity's session id (when ready), build the replay URL, persist it
 * locally so subsequent events carry it, and POST to /clarity/session so ops
 * can join phone ↔ replay-URL in Cassandra.
 */
export function setClaritySession() {
  if (!isBrowser) return;
  let attempts = 0;
  const maxAttempts = 30; // ~15s @ 500ms

  const tryCapture = () => {
    attempts += 1;
    try {
      const sessionId = ensureSessionId();
      if (!window.clarity || typeof window.clarity !== 'function') {
        if (attempts < maxAttempts) setTimeout(tryCapture, 500);
        return;
      }
      // Best path: official Clarity API (v0.7+).
      try {
        window.clarity('set', 'rnr_session_id', sessionId);
      } catch (_err) {
        /* swallow */
      }

      const postClaritySession = (claritySessionId) => {
        if (!claritySessionId) return;
        const url = `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/impressions?SessionId=${encodeURIComponent(
          claritySessionId,
        )}`;
        safeSessionSet(SESSION_STORAGE_KEYS.claritySessionId, claritySessionId);
        safeSessionSet(SESSION_STORAGE_KEYS.claritySessionUrl, url);

        // Only POST once per session so we don't spam the table.
        if (safeSessionGet(SESSION_STORAGE_KEYS.claritySessionPosted) === '1') return;
        safeSessionSet(SESSION_STORAGE_KEYS.claritySessionPosted, '1');
        try {
          const body = JSON.stringify({
            session_id: sessionId,
            clarity_session_url: url,
            platform: 'web',
            device_id: ensureDeviceId(),
          });
          if (
            typeof navigator !== 'undefined' &&
            typeof navigator.sendBeacon === 'function'
          ) {
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon(CLARITY_SESSION_PATH, blob)) return;
          }
          fetch(CLARITY_SESSION_PATH, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
            credentials: 'omit',
          }).catch(() => undefined);
        } catch (_err) {
          /* swallow */
        }
      };

      let captured = false;
      try {
        window.clarity('getSessionId', (claritySessionId) => {
          captured = true;
          postClaritySession(claritySessionId);
        });
      } catch (_err) {
        /* swallow */
      }

      // Fallback: parse the _clsk cookie. The first segment of the cookie
      // value is the Clarity session id.
      setTimeout(() => {
        if (captured) return;
        try {
          const match = document.cookie.match(/_clsk=([^;]+)/);
          if (match && match[1]) {
            const decoded = decodeURIComponent(match[1]);
            const claritySessionId = decoded.split('|')[0];
            if (claritySessionId) postClaritySession(claritySessionId);
          }
        } catch (_err) {
          /* swallow */
        }
      }, 1000);
    } catch (_err) {
      if (attempts < maxAttempts) setTimeout(tryCapture, 500);
    }
  };

  // Defer the first attempt so Clarity has a chance to load.
  setTimeout(tryCapture, 1500);
}

// -----------------------------------------------------------------------------
//   Lifecycle
// -----------------------------------------------------------------------------

export function isAnalyticsOptedOut() {
  return isOptedOut();
}

export function setAnalyticsOptOut(value) {
  safeLocalSet(LOCAL_STORAGE_KEYS.optOut, value ? '1' : '0');
}

/**
 * Wire up the analytics layer. Idempotent. Call once from `src/index.js`
 * before React renders so even the very first click is captured.
 */
export function initAnalytics() {
  if (!isBrowser) return;
  if (isInitialized) return;
  isInitialized = true;
  try {
    // Hydrate identity + restore any unsent events from prior session.
    ensureSessionId();
    ensureDeviceId();
    captureAttributionOnce();

    const persisted = loadPersistedQueue();
    if (persisted.length > 0) {
      inMemoryQueue = persisted;
    }

    // Periodic flush.
    flushTimer = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);

    // Flush on tab hide / unload so we don't lose the last events.
    const unloadHandler = () => {
      void flush();
    };
    window.addEventListener('pagehide', unloadHandler);
    window.addEventListener('beforeunload', unloadHandler);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void flush();
      }
    });

    // Delegated click listener on document.body — captures every tap.
    installClickListener();

    // Kick off Clarity association — fires after a short delay so the
    // Clarity tag has time to bootstrap.
    setClaritySession();
  } catch (_err) {
    /* swallow — never break boot */
  }
}

// Exported only for tests.
export const __INTERNALS__ = {
  SESSION_STORAGE_KEYS,
  LOCAL_STORAGE_KEYS,
  resetForTests() {
    inMemoryQueue = [];
    currentPhone = null;
    isFlushInFlight = false;
    isInitialized = false;
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  },
};
